import { useCalendar } from "../CalendarContext";
import {
  ComponentPropsWithoutRef,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { DateTime, Info } from "luxon";
import { isUnavailableDate } from "./utils";
import { CalendarDay } from "./CalendarDay";

export type CalendarWeekProps = ComponentPropsWithoutRef<"tr"> & {
  /**
   * @deprecated This is overridden internally
   */
  className?: ComponentPropsWithoutRef<"tr">["className"];
  /**
   * The dates for the week.
   */
  week: DateTime[];
};

/**
 * A component that displays a week in the calendar.
 *
 * @param props - The props for the tr element.
 * @returns A component that displays a week in the calendar.
 */
export const CalendarWeek = forwardRef<HTMLTableRowElement, CalendarWeekProps>(
  (props, ref) => {
    const { className, week, ...rest } = props;
    const context = useCalendar();
    const {
      focusedDate,
      setFocusedDate,
      locale,
      maxDate,
      unavailable,
      defaultTimeZone,
      id: uid,
      startDay,
    } = context;

    const initialized = useRef(false);

    const futureDateSelection = useCallback(
      (targetDate: DateTime) => {
        if (maxDate !== null && targetDate.toMillis() > maxDate?.toMillis()) {
          return;
        }

        if (isUnavailableDate(targetDate, unavailable, defaultTimeZone)) {
          let nextFocusDate = targetDate.plus({ days: 1 });
          for (let i = 0; i < 365; i++) {
            if (
              maxDate !== null &&
              nextFocusDate.toMillis() > maxDate?.toMillis()
            ) {
              break;
            }
            if (
              isUnavailableDate(nextFocusDate, unavailable, defaultTimeZone)
            ) {
              nextFocusDate = nextFocusDate.plus({ days: 1 });
            } else {
              // setHoveredDate(nextFocusDate);
              setFocusedDate(nextFocusDate);
              break;
            }
          }
          return;
        }

        setFocusedDate(targetDate);
      },
      [maxDate, setFocusedDate, defaultTimeZone, unavailable],
    );

    let startDayIndex = 6;
    switch (startDay) {
      case "Monday":
        startDayIndex = 0;
        break;
      case "Sunday":
      default:
        startDayIndex = 6;
    }

    const weekdaysArr: string[] = [];
    for (let i = 0; i < 7; i++) {
      const dayIndex = (startDayIndex + i) % 7;
      weekdaysArr.push(Info.weekdays("narrow", { locale })[dayIndex]);
    }

    const firstDay = focusedDate.startOf("month");

    const prevArr: DateTime<boolean>[] = [];
    if (firstDay.weekday !== startDayIndex + 1) {
      // Complete the first row with the previous month's dates.
      for (let i = 1; i < 8; i++) {
        const prevDay = firstDay.minus({ days: i });
        prevArr.push(prevDay);
        if (prevDay.weekday === startDayIndex + 1) break;
      }
    }
    prevArr.reverse();

    useEffect(() => {
      if (!initialized.current) return;
      setTimeout(() => {
        const targetElement = document.getElementById(
          `${uid}-${focusedDate.toISODate()}`,
        );
        targetElement?.focus();
      }, 100);
    }, [focusedDate, uid]);

    useEffect(() => {
      if (!isUnavailableDate(focusedDate, unavailable, defaultTimeZone)) return;
      futureDateSelection(focusedDate.plus({ days: 1 }));
    }, [focusedDate, futureDateSelection, defaultTimeZone, unavailable]);

    return (
      <tr {...rest} ref={ref}>
        {week.map((n) => {
          return <CalendarDay date={n} key={n.toISODate()} />;
        })}
      </tr>
    );
  },
);

CalendarWeek.displayName = "CalendarWeek";
